'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { HeartIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { useAuth } from '@/providers/userContext';
import { fetchJson } from '@/lib/client/fetchJson';
import { getAllSaved, getAllVisited, subscribe, getAllGuestSaved } from '@/lib/client/interactionCache';
import Toggle from '@/components/atoms/Toggle';
import NewsBG from '@/assets/images/news-bg.png';
import MoNewsBG from '@/assets/images/mo-news-bg.png';
import UIButton from '@/components/atoms/UIButtons/UIButton';
import FeatureFlag from '@/shared/features';
function formatVisitDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US');
}
function formatOrdinalRank(value) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const int = Math.floor(n);
  const mod100 = int % 100;
  let suffix = 'th';
  if (mod100 < 11 || mod100 > 13) {
    const mod10 = int % 10;
    if (mod10 === 1) suffix = 'st';else if (mod10 === 2) suffix = 'nd';else if (mod10 === 3) suffix = 'rd';
  }
  return `${int.toLocaleString('en-US')}${suffix}`;
}
export default function MyHeartsPage({
  mode = 'wishlist',
  initialHearts = null
}) {
  const {
    user
  } = useAuth();
  const router = useRouter();
  const isWishlist = mode === 'wishlist';
  const title = isWishlist ? 'My Hearts' : 'My Hearts';
  const emptyMessage = isWishlist ? 'No saved hearts yet.' : 'No visited hearts yet.';
  const [hearts, setHearts] = useState(initialHearts || []);
  const [savedHearts, setSavedHearts] = useState([]);
  const [visitedHearts, setVisitedHearts] = useState([]);
  const [loading, setLoading] = useState(!initialHearts); // Skip loading if server data provided
  const [error, setError] = useState('');
  const [rank, setRank] = useState(null);

  // Track previous user to detect actual changes
  const prevUserRef = useRef(user);
  const load = useCallback(async cancelled => {
    try {
      setLoading(true);
      setError('');
      if (!user || !user.id) {
        // For guest users, only fetch hearts if not already provided server-side
        let heartsData = initialHearts;
        if (!heartsData) {
          const heartsResponse = await fetchJson('/api/hearts', {
            method: 'GET'
          });
          heartsData = heartsResponse?.data || [];
        }
        if (cancelled) return;
        setSavedHearts(getAllGuestSaved());
        setVisitedHearts([]);
        setHearts(heartsData);
        setRank(null);
      } else {
        // For authenticated users, fetch interactions and rank (only fetch rank for visited mode)
        const fetchPromises = [fetchJson('/api/me/interactions', {
          method: 'GET'
        })];

        // Only fetch rank data when on visited page
        if (mode === 'visited') {
          fetchPromises.push(fetchJson('/api/me/rank', {
            method: 'GET'
          }));
        }
        const results = await Promise.all(fetchPromises);
        const interactions = results[0];
        const rankResponse = mode === 'visited' ? results[1] : null;
        if (cancelled) return;
        setSavedHearts(interactions?.savedHearts || []);
        setVisitedHearts(interactions?.visitedHearts || []);
        setRank(rankResponse?.rank || null);

        // Use initialHearts if provided, otherwise fetch from API
        if (!initialHearts) {
          const heartsResponse_0 = await fetchJson('/api/hearts', {
            method: 'GET'
          });
          setHearts(heartsResponse_0?.data || []);
        } else {
          setHearts(initialHearts);
        }
      }
    } catch (err) {
      if (cancelled) return;
      setError(err?.message || 'Failed to load hearts.');
    } finally {
      if (!cancelled) setLoading(false);
    }
  }, [user, mode, initialHearts]);
  useEffect(() => {
    let cancelled_0 = false;
    load(cancelled_0);
    return () => {
      cancelled_0 = true;
    };
  }, [load]);
  useEffect(() => {
    // Only reload if user actually changed (login/logout in another tab)
    const prevUserId = prevUserRef.current?.id;
    const currentUserId = user?.id;
    if (prevUserId !== currentUserId) {
      load(false);
    }

    // Update ref for next comparison
    prevUserRef.current = user;
  }, [user, load]);
  useEffect(() => {
    const unsubscribe = subscribe(() => {
      setSavedHearts(!user ? getAllGuestSaved() : getAllSaved());
      setVisitedHearts(!user ? [] : getAllVisited());
    });
    return unsubscribe;
  }, [user]);
  const savedOrder = useMemo(() => savedHearts.map(id => Number(id)).filter(id_0 => !Number.isNaN(id_0)), [savedHearts]);
  const visitedOrder = useMemo(() => visitedHearts.map(visit => ({
    heartId: Number(visit.heartId),
    visitedAt: visit.VisitedAt || visit.visitedAt || null
  })).filter(visit_0 => !Number.isNaN(visit_0.heartId)), [visitedHearts]);
  const heartsById = useMemo(() => {
    const map = new Map();
    hearts.forEach(heart => {
      map.set(Number(heart.id), heart);
    });
    return map;
  }, [hearts]);
  const visitedById = useMemo(() => {
    const map_0 = new Map();
    visitedOrder.forEach(visit_1 => {
      map_0.set(visit_1.heartId, visit_1.visitedAt);
    });
    return map_0;
  }, [visitedOrder]);
  const displayItems = useMemo(() => {
    const sortByTitle = (a, b) => {
      const nameA = a?.title || a?.name || '';
      const nameB = b?.title || b?.name || '';
      return nameA.localeCompare(nameB, undefined, {
        sensitivity: 'base'
      });
    };
    if (isWishlist) {
      return savedOrder.map(id_1 => heartsById.get(id_1)).filter(Boolean).sort(sortByTitle);
    }
    return visitedOrder.map(visit_2 => heartsById.get(visit_2.heartId)).filter(Boolean).sort(sortByTitle);
  }, [heartsById, isWishlist, savedOrder, visitedOrder]);
  const visitedCount = visitedOrder.length;
  //const savedCount = savedOrder.length;
  const totalCount = hearts.length;
  const summaryCount = visitedCount;
  const summaryLabel = 'Hearts Visited';
  const listTitle = isWishlist ? 'Saved' : 'Visited';
  const toggleOptions = [{
    label: 'Saved',
    value: 'wishlist'
  }, {
    label: 'Visited',
    value: 'visited'
  }];
  return <div className="relative xl:pt-25 pt-21  2xl:pb-50 lg:pb-40 pb-37">
      <div className="absolute pointer-events-none left-auto right-0 top-0 z-[-1] xl:max-w-[50%] sm:max-w-[60%] max-w-[85%] w-full">
        <Image src={NewsBG} alt="" className="sm:block hidden" aria-hidden="true" priority sizes="100vw" />
        <Image src={MoNewsBG} alt="" className="sm:hidden block w-full" aria-hidden="true" priority sizes="100vw" />
      </div>

      <div className="container-wrap">
        <h1 className="font-semibold text-[#323232] 2xl:text-[68px] xl:text-[54px] lg:text-[48px] sm:text-[40px] text-[26px] leading-[1em]">
          {title}
        </h1>
        <div className="flex flex-wrap items-center gap-4">
          <div className="xl:mt-8 lg:mt-6 mt-4 max-w-[340px]">
            <Toggle options={toggleOptions} defaultValue={isWishlist ? 'wishlist' : 'visited'} className="sm:w-full lg:w-[339px]" onChange={value => router.push(value === 'visited' ? '/account/visited' : '/account/wishlist')} />
          </div>
          <FeatureFlag featureName="customMap">
            <UIButton rounded="full" href="/explore?view=map&showClusters=false&fadeIcons=true" variant="outline" className="xl:mt-8 lg:mt-6 mt-4 xl:px-6 md:px-5 px-4 lg:py-2 py-1.5 xl:text-base md:text-sm text-[13px] font-medium">
              <span className="flex lg:h-5 h-4 items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <mask id="mask0_11871_1189" style={{
                  maskType: 'alpha'
                }} maskUnits="userSpaceOnUse" x="0" y="0" width="15" height="15">
                    <rect width="15" height="15" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_11871_1189)">
                    <path d="M8.38281 7.13281C8.6276 6.88802 8.75 6.59375 8.75 6.25C8.75 5.90625 8.6276 5.61198 8.38281 5.36719C8.13802 5.1224 7.84375 5 7.5 5C7.15625 5 6.86198 5.1224 6.61719 5.36719C6.3724 5.61198 6.25 5.90625 6.25 6.25C6.25 6.59375 6.3724 6.88802 6.61719 7.13281C6.86198 7.3776 7.15625 7.5 7.5 7.5C7.84375 7.5 8.13802 7.3776 8.38281 7.13281ZM7.5 13.75C5.82292 12.3229 4.57031 10.9974 3.74219 9.77344C2.91406 8.54948 2.5 7.41667 2.5 6.375C2.5 4.8125 3.0026 3.56771 4.00781 2.64063C5.01302 1.71354 6.17708 1.25 7.5 1.25C8.82292 1.25 9.98698 1.71354 10.9922 2.64063C11.9974 3.56771 12.5 4.8125 12.5 6.375C12.5 7.41667 12.0859 8.54948 11.2578 9.77344C10.4297 10.9974 9.17708 12.3229 7.5 13.75Z" fill="#1C1B1F" />
                  </g>
                </svg>
              </span>
              <span className="pl-2">Map</span>
            </UIButton>
          </FeatureFlag>
        </div>
        <div className="2xl:mt-8 lg:mt-6 mt-4 grid gap-8 lg:grid-cols-[minmax(0,582px)_minmax(0,516px)] lg:items-start lg:gap-4 sm:mx-0 -mx-4">
          {mode && mode == 'visited' && <section className="lg:order-2 lg:max-w-[516px] w-full lg:mt-7">
              <div className="flex flex-wrap gap-4 rounded-[14px] bg-white sm:py-4.5 py-4 lg:px-7 sm:px-6 px-4 shadow-[-2px_2px_20px_0_rgba(0,0,0,0.06)]">
                <div>
                  <div className="flex items-end gap-2">
                    <span className="2xl:text-[120px] xl:text-[104px] text-[80px] leading-[0.78] font-semibold text-[#323232] ">
                      {summaryCount.toLocaleString()}
                    </span>
                    <span className="xl:text-[22px] lg:text-lg leading-[0.8] text-base font-medium text-[#323232]">
                      of {totalCount.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm leading-[1.5] mt-1 font-normal text-[#626262]">
                    {summaryLabel}
                  </p>
                </div>
                <div className="ml-auto text-right">
                  <p className="lg:text-sm text-[13px] text-[#626262] font-normal leading-[1.5]">
                    Rank:{' '}
                    <span className="text-[#323232] font-semibold">
                      {formatOrdinalRank(rank) ?? '—'}
                    </span>
                  </p>
                  <Link href="/leaderboard" className="lg:mt-4 mt-3 inline-flex items-center justify-center rounded-full backdrop-blur-[10px] bg-[#F5F5F5] px-6 py-3.25 text-sm font-semibold text-[#323232] leading-[1.22] hover:bg-[#ededed]">
                    Leaderboard
                  </Link>
                </div>
              </div>
            </section>}

          <section className="lg:order-1 lg:max-w-[582px] w-full">
            <h2 className="lg:text-lg text-base leading-[1] font-semibold text-[#323232] sm:px-0 px-4">
              {listTitle}
            </h2>
            <div className="mt-3 overflow-hidden rounded-[14px] bg-white shadow-[-2px_2px_20px_0_rgba(0,0,0,0.06)]">
              {loading && <>
                  <HeartRowSkeleton />
                  <HeartRowSkeleton />
                  <HeartRowSkeleton isLast />
                </>}
              {!loading && error && <p className="p-4 text-sm text-red-600">{error}</p>}
              {!loading && !error && displayItems.length === 0 && <p className="p-4 text-base text-[#626262]">{emptyMessage}</p>}
              {!loading && !error && displayItems.map((heart_0, index) => <HeartRow key={heart_0.id} heart={heart_0} mode={mode} visitedAt={visitedById.get(Number(heart_0.id))} isLast={index === displayItems.length - 1} />)}
            </div>
          </section>
        </div>
      </div>
    </div>;
}
function HeartRowSkeleton(t0) {
  const $ = _c(5);
  const {
    isLast: t1
  } = t0;
  const isLast = t1 === undefined ? false : t1;
  const t2 = `flex items-center justify-between gap-4 py-2.5 pl-4 sm:pr-5.5 pr-4 animate-pulse ${isLast ? "" : "border-b border-[#DDDDDD]/85"}`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="relative lg:h-14.5 lg:w-14.5 w-12.5 h-12.5 flex-shrink-0 overflow-hidden rounded-full bg-neutral-200" />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-3">{t3}<div className="flex flex-col gap-2"><div className="lg:h-5 h-4.5 lg:w-48 w-40 bg-neutral-200 rounded" /><div className="lg:h-4 h-3.5 lg:w-32 w-28 bg-neutral-200 rounded" /></div></div>;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex items-center text-right gap-1.75"><div className="lg:h-3.5 h-3 w-12 bg-neutral-200 rounded" /><div className="h-4 w-4 bg-neutral-200 rounded-full" /></div>;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== t2) {
    t6 = <div className={t2}>{t4}{t5}</div>;
    $[3] = t2;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  return t6;
}
const HeartRow = memo(function HeartRow(t0) {
  const $ = _c(26);
  const {
    heart,
    mode,
    visitedAt,
    isLast
  } = t0;
  const isWishlist = mode === "wishlist";
  const imageSrc = heart?.imagesWithAltText?.[0]?.src || heart?.heroImage || "";
  const imageAlt = heart?.imagesWithAltText?.[0]?.alt || heart?.title || "Heart image";
  let t1;
  if ($[0] !== isWishlist || $[1] !== visitedAt) {
    t1 = isWishlist ? "" : formatVisitDate(visitedAt);
    $[0] = isWishlist;
    $[1] = visitedAt;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const visitDate = t1;
  const visitedLabel = visitDate ? `Visited ${visitDate}` : "Visited";
  const t2 = `/hearts/${heart.slug}`;
  const t3 = `flex items-center justify-between gap-4 py-2.5 pl-4 sm:pr-5.5 pr-4 transition hover:bg-neutral-50 ${isLast ? "" : "border-b border-[#DDDDDD]/85"}`;
  let t4;
  if ($[3] !== imageAlt || $[4] !== imageSrc) {
    t4 = imageSrc ? <Image src={imageSrc} alt={imageAlt} fill={true} loading="lazy" className="object-cover lg:h-14.5 lg:w-14.5 w-12.5 h-12.5" /> : null;
    $[3] = imageAlt;
    $[4] = imageSrc;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <div><div className="relative lg:h-14.5 lg:w-14.5 w-12.5 h-12.5 flex-shrink-0 overflow-hidden rounded-full bg-neutral-100">{t4}</div></div>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== heart.title) {
    t6 = <p className="lg:text-lg text-base leading-[1.1] font-semibold text-[#323232] mb-0.5 line-clamp-1">{heart.title}</p>;
    $[8] = heart.title;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== heart.locationName) {
    t7 = <p className="lg:text-sm text-[13px] text-[#626262] leading-[1.2] line-clamp-2">{heart.locationName}</p>;
    $[10] = heart.locationName;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <div>{t6}{t7}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t5 || $[16] !== t8) {
    t9 = <div className="flex items-center gap-3">{t5}{t8}</div>;
    $[15] = t5;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== isWishlist || $[19] !== visitedLabel) {
    t10 = <div className="flex items-center text-right gap-1.75 lg:text-sm text-[13px] font-normal text-[#626262] leading-[1]">{isWishlist ? <><span>Saved</span><HeartIcon className="h-4 w-4 text-[#D022BC] flex-shrink-0" /></> : <><span className="max-w-[70px]">{visitedLabel}</span><CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-[#00CE59]" /></>}</div>;
    $[18] = isWishlist;
    $[19] = visitedLabel;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t2 || $[23] !== t3 || $[24] !== t9) {
    t11 = <Link href={t2} className={t3}>{t9}{t10}</Link>;
    $[21] = t10;
    $[22] = t2;
    $[23] = t3;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
});